import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "email", "form", "success"]
  
  connect() {
    document.addEventListener('turbo:load', this.handleTurboLoad)
    document.addEventListener('turbo:render', this.handleTurboRender)
    this.startModalTimer()
    this.setupForm()
  }

  async setupForm() {
    try {
      try {
        const tokenResp = await fetch('https://survivalcollective.com/api/subscribe.php');
        const {token} = await tokenResp.json();
        
        // Set CSRF token for both forms if they exist
        const modalToken = document.getElementById('modal_csrf_token');
        const inlineToken = document.getElementById('inline_csrf_token');
        if (modalToken) modalToken.value = token;
        if (inlineToken) inlineToken.value = token;
        
        // Set screen resolution and timezone for both forms
        const screenRes = `${window.screen.width}x${window.screen.height}`;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        document.querySelectorAll('input[name="screen_res"]').forEach(input => {
          input.value = screenRes;
        });
        
        document.querySelectorAll('input[name="timezone"]').forEach(input => {
          input.value = timezone;
        });
      } catch (err) {
        console.warn('Error fetching CSRF token:', err);
      }
      
      // Setup inline form submission
      const inlineForm = document.getElementById('inline-newsletter-form');
      if (inlineForm) {
        inlineForm.addEventListener('submit', this.handleInlineSubmit.bind(this));
      }
    } catch (err) {
      console.error('Error setting up newsletter forms:', err);
    }
  }

  disconnect() {
    document.removeEventListener('turbo:load', this.handleTurboLoad)
    document.removeEventListener('turbo:render', this.handleTurboRender)
    if (this.modalTimeout) clearTimeout(this.modalTimeout)
  }

  debug(message) {
    const params = new URLSearchParams(window.location.search)
    if (params.get('debug') === 'newsletter') {
      console.log(`[Newsletter Modal] ${message}`)
    }
  }

  handleTurboLoad = () => {
    this.startModalTimer()
  }

  handleTurboRender = () => {
    this.startModalTimer()
  }

  startModalTimer() {
    if (this.modalTimeout) clearTimeout(this.modalTimeout)
    
    this.modalTimeout = setTimeout(() => {
      this.debug('Checking modal conditions...')
      if (this.shouldShowModal()) {
        this.debug('Opening modal...')
        this.openModal()
      }
    }, 3000)
  }

  shouldShowModal() {
    // Rule 3: Submit the form - shouldn't show for a year
    const waitlistJoined = localStorage.getItem('newsletterWaitlistJoined')
    if (waitlistJoined) {
      const joinedDate = new Date(waitlistJoined)
      const oneYearAgo = new Date()
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
      if (joinedDate > oneYearAgo) {
        this.debug('Modal hidden: User joined waitlist within last year')
        return false
      }
    }

    // Rule 2: Click X and reload - shouldn't show until tomorrow
    const dailyClosed = localStorage.getItem('newsletterDailyClosed')
    if (dailyClosed) {
      const closedDate = new Date(dailyClosed)
      const today = new Date()
      if (closedDate.toDateString() === today.toDateString()) {
        this.debug('Modal hidden: Closed with X today')
        return false
      }
    }

    this.debug('Modal allowed to show')
    return true
  }

  openModal() {
    this.modalTarget.classList.remove('hidden')
    this.modalTarget.classList.add('fade-in')
    setTimeout(() => {
      if (this.hasEmailTarget) this.emailTarget.focus()
    }, 100)
  }

  closeModal(type = 'OUTSIDE') {
    this.modalTarget.classList.add('fade-out')
    
    setTimeout(() => {
      this.modalTarget.classList.add('hidden')
      this.modalTarget.classList.remove('fade-out')
    }, 300)
    
    if (type === 'X') {
      // Rule 2: Don't show until tomorrow
      localStorage.setItem('newsletterDailyClosed', new Date().toISOString())
      this.debug('Modal closed with X: Hidden until tomorrow')
    } else {
      this.debug('Modal closed by clicking outside: Will show on reload')
    }
  }

  closeWithX(event) {
    event.preventDefault()
    this.closeModal('X')
  }

  clickOutside(event) {
    if (event.target === this.modalTarget) {
      this.closeModal('OUTSIDE')
    }
  }

  async handleInlineSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const status = document.getElementById('inline-status');
    const statusText = status.querySelector('p');
    
    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form)
      });
      
      const data = await response.json();
      status.classList.remove('hidden');
      
      if (!response.ok) throw new Error(data.error);
      
      statusText.textContent = 'Thanks for subscribing!';
      statusText.className = 'text-sm text-green-400';
      form.reset();
      
      // Store subscription in localStorage
      localStorage.setItem('newsletterWaitlistJoined', new Date().toISOString());
    } catch (err) {
      statusText.textContent = err.message;
      statusText.className = 'text-sm text-red-400';
    }
  }

  async submitForm(event) {
    event.preventDefault();
    
    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        body: new FormData(this.formTarget)
      });
      
      const data = await response.json();
      
      if (!response.ok) throw new Error(data.error);
      
      // Store subscription in localStorage
      localStorage.setItem('newsletterWaitlistJoined', new Date().toISOString());
      
      // Show success message
      this.formTarget.classList.add('hidden');
      this.successTarget.classList.remove('hidden');
      
      // Close after delay
      setTimeout(() => {
        this.closeModal();
      }, 2000);
    } catch (err) {
      console.error('Newsletter submission error:', err);
      alert(err.message);
    }
  }
}
